import { useCallback, useEffect, useRef, useState } from 'react';
import { LayoutProps } from './VideoViewsLayout';
import { VideoAsset } from '$Utilities/models/videoViews';
import { Box, Button, Dialog, DialogContent, Drawer } from '@mui/material';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import { useVideoPlayerSync } from '$State/VideoPlayerSync/VideoPlayerSyncContext';
import { useAiOverlayContext } from '$State/AiOverlay/AiOverlayContext';
import { React } from 'Imports';
import { isEncompass } from '$Utilities/integrationPlatform';
import SidePanelVerticalButton from './SidePanelVerticalButton';
import { ConfigService } from '$State/ConfigFreezerService';

const styles = require('./Layouts.scss') as {
    closeIconContainer: string;
    mainFullViewBox: string;
    closeIcon: string;
    smallerFullViewBox: string;
    panelButtonContainer: string;
    panelButton: string;
};

interface FullscreenViewProps extends LayoutProps {
    onCloseFullScreen?: () => void;
    numberOfChannels?: number;
    downloadable?: boolean;
    // Seconds into the main video to seek to once it mounts, so opening fullscreen picks up where the
    // caller's own (smaller) player left off instead of always restarting at 0.
    initialTimeSeconds?: number;
    // Reported for the main video only (not the channel-switch thumbnails), so a caller like the recall
    // preview can mirror fullscreen scrubbing/playback back into its own selection state.
    onPlaybackProgress?: (currentTime: number, duration: number, framesPerSecond?: number) => void;
}

const resolveVideoUrl = (video: VideoAsset | undefined, aiOverlayOn: boolean, hasAiOverlayEverBeenOn: boolean): string => {
    if (aiOverlayOn && hasAiOverlayEverBeenOn && video?.aiUrl) {
        return video.aiUrl;
    }
    return video?.url ?? '';
};

const FullscreenView = (props: FullscreenViewProps) => {
    const { isFullView } = useVideoPlayerSync();
    const { aiOverlayOn, hasAiOverlayEverBeenOn } = useAiOverlayContext();
    const [mainVideo, setMainVideo] = useState<VideoAsset | undefined>();
    const [otherVideos, setOtherVideos] = useState<VideoAsset[] | undefined>();
    const [isDrawerOpen, setIsDrawerOpen] = useState(true);
    const containerRef = useRef(null);
    const [drawerWidth, setDrawerWidth] = useState(0);
    const drawerObserverRef = useRef<ResizeObserver | null>(null);
    const fullscreenSizeRef = useRef<HTMLDivElement>(null);
    const [dialogMarginLeft, setDialogMarginLeft] = useState<string | undefined>(undefined);
    const mainSeekRef = useRef<((time: number) => void) | null>(null);

    const drawerPaperRef = useCallback((node: HTMLDivElement | null) => {
        if (drawerObserverRef.current) {
            drawerObserverRef.current.disconnect();
            drawerObserverRef.current = null;
        }
        if (node) {
            const observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    setDrawerWidth(entry.contentRect.width);
                }
            });
            observer.observe(node);
            drawerObserverRef.current = observer;
            setDrawerWidth(node.getBoundingClientRect().width);
        }
    }, []);

    useEffect(() => {
        if (mainVideo === undefined && otherVideos === undefined && props.videoAssets.length > 0) {
            setMainVideo(props.videoAssets.find((asset) => asset.channelNumber === props.primaryChannelNumber));
            setOtherVideos(props.videoAssets.filter((asset) => asset.channelNumber !== props.primaryChannelNumber));
        }
    }, [props.videoAssets]);

    // Fires once, right after the main video mounts, to pick up where the caller's own player left off.
    useEffect(() => {
        if (mainVideo && props.initialTimeSeconds) {
            mainSeekRef.current?.(props.initialTimeSeconds);
        }
    }, [mainVideo]);

    const handleModalClose = () => {
        props.onCloseFullScreen?.();
    };

    const handleDrawerToggle = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    const handleChangeMainVideo = (channelNumber: number | undefined) => {
        if (otherVideos && mainVideo) {
            const indexVideoSelected = otherVideos.findIndex((asset) => asset.channelNumber === channelNumber) ?? 0;

            if (indexVideoSelected !== -1) {
                const newArray = [...otherVideos.map((item) => item)];
                newArray[indexVideoSelected] = mainVideo ?? ({} as VideoAsset);

                setMainVideo(otherVideos[indexVideoSelected]);
                setOtherVideos(newArray);
            }
        }
    };

    const getShowVideosButtonText = () => {
        if (isEncompass) {
            return isDrawerOpen ? 'Hide Videos' : 'Show Videos';
        }
        return isDrawerOpen ? 'HIDE VIDEOS' : 'SHOW VIDEOS';
    };

    useEffect(() => {
        if (isEncompass || ConfigService.isRunningStandAlone()) return;

        // Adjust dialog margin based on main GEOTAB menu state to avoid overlap fullscreen with menu
        // Encompass and Standalone version are working fine with a centered Full Screen Dialog.
        const btn = document.querySelector('.geo-main-menu__toggler.innerMenuToggle');
        if (!btn) return;

        const updateMargin = () => {
            setDialogMarginLeft(btn.getAttribute('aria-expanded') === 'true' ? '256px' : '56px');
        };
        updateMargin();

        const menuObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'aria-expanded') updateMargin();
            });
        });
        menuObserver.observe(btn, { attributes: true });

        return () => menuObserver.disconnect();
    }, []);

    return (
        <div>
            <Dialog
                open={isFullView}
                onClose={handleModalClose}
                PaperProps={{
                    sx: {
                        bgcolor: 'transparent',
                        width: 'auto',
                        maxHeight: '100%',
                        boxShadow: 'none',
                        margin: isEncompass && ConfigService.isRunningEncompassIntegrated() ? '25px auto auto auto' : 'auto',
                    },
                }}
                fullWidth={true}
                maxWidth={false}
                sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                slotProps={{ backdrop: { style: { backgroundColor: 'rgba(0, 0, 0, 0.75)' } } }}
            >
                <DialogContent
                    ref={fullscreenSizeRef}
                    sx={{
                        padding: 'unset',
                        height: 'fit-content',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        marginLeft: dialogMarginLeft,
                    }}
                >
                    <div
                        className={styles.closeIconContainer}
                        style={{
                            width: !isEncompass && dialogMarginLeft == '256px' ? '74vw' : '80vw',
                            flexShrink: 0,
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                        }}
                    >
                        <Button className={styles.closeIcon} title="Exit Full Screen" onClick={handleModalClose}>
                            Exit Full Screen
                        </Button>
                    </div>
                    <Box
                        className={styles.mainFullViewBox}
                        sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            minWidth: !isEncompass && dialogMarginLeft == '256px' ? '74vw' : '80vw',
                            maxHeight: '80vh',
                            aspectRatio: '16/9',
                            display: 'flex',
                            justifyContent: 'center',
                            width: 'min-content',
                        }}
                    >
                        <div
                            id="full-main-video-container"
                            ref={containerRef}
                            style={{ position: 'relative', width: '100%', height: '100%' }}
                        >
                            <VideoPlayer
                                src={resolveVideoUrl(mainVideo, aiOverlayOn, hasAiOverlayEverBeenOn)}
                                videoType={mainVideo?.type ?? 'FrontVideo'}
                                channelNumber={mainVideo?.channelNumber}
                                forFullscreenView={true}
                                onCloseFullScreen={handleModalClose}
                                title={
                                    mainVideo?.channelNumber != null
                                        ? `Channel ${mainVideo.channelNumber} Video`
                                        : (mainVideo?.type ?? 'FrontVideo')
                                }
                                maxHeight={'80vh'}
                                downloadable={props.downloadable}
                                seekRef={mainSeekRef}
                                onPlaybackProgress={props.onPlaybackProgress}
                            ></VideoPlayer>
                            {props.numberOfChannels && props.numberOfChannels > 1 && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        right: isDrawerOpen ? `${drawerWidth}px` : '0',
                                        top: 0,
                                        transition: 'right 0.3s ease',
                                    }}
                                >
                                    <SidePanelVerticalButton text={getShowVideosButtonText()} onClick={handleDrawerToggle} />
                                </div>
                            )}

                            <Drawer
                                anchor="right"
                                open={isDrawerOpen}
                                onClose={() => setIsDrawerOpen(false)}
                                hideBackdrop
                                variant="persistent"
                                ModalProps={{
                                    container: containerRef.current,
                                    disableRestoreFocus: true,
                                    disableAutoFocus: true,
                                    style: {
                                        position: 'absolute',
                                    },
                                }}
                                PaperProps={{
                                    ref: drawerPaperRef,
                                    sx: {
                                        position: 'absolute',
                                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                                        maxWidth: '248px',
                                        overflow: 'hidden',
                                        zIndex: 0,
                                        bottom: 25,
                                        border: 'none',
                                    },
                                }}
                            >
                                {props.numberOfChannels && props.numberOfChannels >= 2 && (
                                    <Box
                                        sx={{
                                            padding: '24px',
                                        }}
                                    >
                                        <div style={{ color: 'white', fontSize: '14px', marginBottom: '16px' }}>
                                            Click on a video below to play it in full screen.
                                        </div>

                                        {otherVideos?.map((video, idx) => (
                                            <Box
                                                key={idx + (video.channelNumber?.toString() ?? video.type)}
                                                className={styles.smallerFullViewBox}
                                                onClick={() => handleChangeMainVideo(video.channelNumber)}
                                                title="Play in Full Screen"
                                            >
                                                <VideoPlayer
                                                    src={resolveVideoUrl(video, aiOverlayOn, hasAiOverlayEverBeenOn)}
                                                    videoType={video.type}
                                                    channelNumber={video.channelNumber}
                                                    title={
                                                        video.channelNumber != null ? `Channel ${video.channelNumber} Video` : video.type
                                                    }
                                                    hideControls={true}
                                                    forFullscreenView={true}
                                                    isSmallView
                                                ></VideoPlayer>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Drawer>
                        </div>
                    </Box>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default FullscreenView;
